import React, { useState, useRef } from 'react'
import { useTranslation } from 'next-i18next'
import { DEFAULT_THEME_COLORS } from '../../../../css/theme'
import styled from '@emotion/styled'
import useFlow from 'modules/flow/state/use-flow'
import FlowOneColumnStepLayout from '../one-column-step-layout/one-column-step-layout'
import ScrollableList, { ScrollableListData } from '../../../ui/molecules/ScrollableList'
import useOrder from '../../../order/state/order/use-order'
import Row from '../../../ui/atoms/Layout/Row'
import useDoubleTheDonationCompaniesSearch from '../../../order/components/double-the-donation/use-double-the-donation-companies-search'
import {
  trackFormSubmitEvent,
  trackFormErrorEvent,
  trackFormSkipEvent,
} from '../../../analytics/helpers/track-form-events'
import useConfig from '../../../config/state/use-config'

const DoubleTheDonationStep = () => {
  const { t } = useTranslation()
  const flow = useFlow()
  const order = useOrder()
  const config = useConfig()

  const primaryColor =
    config.theme['--theme-color__brand-primary'] ||
    DEFAULT_THEME_COLORS['--theme-color--neutral-10']

  const [query, setQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const lastTrackedErrorRef = useRef<string | null>(null) // Ref to track last error

  const [selectedCompanyId, setSelectedCompanyId] = useState<number | undefined>(
    order.doubleTheDonation.value?.companyId,
  )

  const [selectedCompanyName, setSelectedCompanyName] = useState<string | undefined>(
    order.doubleTheDonation.value?.companyName,
  )

  const [isSearchActive] = useState(selectedCompanyName === '' ? false : true)

  const { data, companies, hasMore, loading, error } = useDoubleTheDonationCompaniesSearch(
    query,
    pageNumber,
  )

  const goNext = () => {
    const nextStep = flow.nextStep

    if (selectedCompanyId && selectedCompanyName) {
      const doubleTheDonationPayload = {
        companyId: selectedCompanyId,
        companyName: selectedCompanyName,
      }

      order.doubleTheDonation.setValue(doubleTheDonationPayload)
      order.doubleTheDonation.save(doubleTheDonationPayload)

      // Track form submission event
      trackFormSubmitEvent(flow.currentStep.name, {
        company_match: selectedCompanyId,
        name: selectedCompanyName,
      })
    } else {
      // If no company selected, track form error
      const currentError = 'No company selected'

      if (lastTrackedErrorRef.current !== currentError) {
        trackFormErrorEvent(flow.currentStep.name, currentError)

        lastTrackedErrorRef.current = currentError
      }
    }

    if (nextStep) {
      flow.goToStep(nextStep.name)
    }
  }

  const onSkip = () => {
    trackFormSkipEvent(flow.currentStep.name)

    goNext()
  }

  const onSelect = ({ value }: ScrollableListData) => {
    const company = companies.find((company) => company.id === Number(value))

    if (company) {
      setSelectedCompanyId(value)
      setSelectedCompanyName(company.company_name)
    }
  }

  const setCompany = () => {
    if (selectedCompanyId && selectedCompanyName) {
      order.doubleTheDonation.setValue({
        companyId: selectedCompanyId,
        companyName: selectedCompanyName,
      })
    }
  }

  const onFetch = (query: string, pageNumber: number) => {
    setQuery(query)
    setPageNumber(pageNumber)
  }

  const resetSearch = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    setSelectedCompanyId(undefined)
    setSelectedCompanyName('')
  }

  return (
    <>
      <FlowOneColumnStepLayout.Main
        title={flow.currentStep.title}
        backButtonDataId="double-the-donation-step-back-button"
      >
        <Wrapper>
          <Row style={{ paddingTop: '24px', paddingBottom: '24px' }}>
            {t(
              'DoubleTheDonationStep.employerCheck',
              'Check if your employer will match your donation. You may receive an email on how to submit your match request.',
            )}
          </Row>
          {!selectedCompanyName && (
            <Row style={{ flexDirection: 'column', gap: '8px' }}>
              <Row>{t('DoubleTheDonationStep.searchForCompany', 'Search for company')}</Row>

              <ScrollableList
                height={200}
                data={data}
                error={error}
                loading={loading}
                hasMore={hasMore}
                onSelect={onSelect}
                fetchNewData={onFetch}
                selected={selectedCompanyId ? selectedCompanyId : ''}
                isActivatedSearch={isSearchActive}
                dataId="double-the-donation-step"
              />
            </Row>
          )}

          {selectedCompanyName && (
            <Row style={{ flexDirection: 'column', gap: '8px' }}>
              <Row>{t('DoubleTheDonationStep.companySelectedLabel', 'Company selected:')}</Row>
              <Row style={{ fontWeight: '700' }}>{selectedCompanyName}</Row>
              <Row style={{ fontSize: '12px' }}>
                <a
                  href={''}
                  onClick={resetSearch}
                  data-id="double-the-donation-step-choose-another-company"
                >
                  {t('DoubleTheDonationStep.chooseAnotherCompany', 'Choose another company')}{' '}
                </a>
              </Row>
            </Row>
          )}
        </Wrapper>
      </FlowOneColumnStepLayout.Main>
      <FlowOneColumnStepLayout.Footer>
        <FlowOneColumnStepLayout.FooterButton
          dataId="double-the-donation-step-skip-button"
          color={DEFAULT_THEME_COLORS['--theme-color--neutral-100']}
          borderColor={DEFAULT_THEME_COLORS['--theme-color--neutral-80']}
          onClick={onSkip}
        >
          {t('DoubleTheDonationStep.skipButton', 'Skip')}
        </FlowOneColumnStepLayout.FooterButton>
        <FlowOneColumnStepLayout.FooterButton
          dataId="double-the-donation-step-continue-button"
          color={primaryColor}
          onClick={() => {
            setCompany()
            goNext()
          }}
        >
          {t('DoubleTheDonationStep.continueButton', 'Continue')}
        </FlowOneColumnStepLayout.FooterButton>
      </FlowOneColumnStepLayout.Footer>
    </>
  )
}

export default DoubleTheDonationStep

const Wrapper = styled.div`
  width: 500px;
  @media (max-width: 767px) {
    text-align: center;
    max-width: 345px;
    div {
      align-items: center;
    }
  }
`
